import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { createSerialColumn, DataTable, WrappingCellText } from '@/components/common/DataTable';
import { RowActionButton } from '@/components/common/RowActions';
import { createActionsColumn } from '@/components/common/DataTable';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { isProjectOpen } from '../utils/projectWindow';
import type { QuestionProject } from '../types/question.types';

const getClosedProjectHint = (deadlineDate: string | null): string =>
  deadlineDate === null
    ? 'This project has no deadline set'
    : `Deadline passed on ${formatDisplayDate(deadlineDate)}`;

interface LoadMoreProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: boolean;
}

interface CollectionProjectsTableProps {
  projects: QuestionProject[];
  isLoading: boolean;
  onSelect: (projectId: number) => void;
  openOnly?: boolean | undefined;
  emptyMessage?: string | undefined;
  emptyHint?: string | undefined;
  loadMoreProps?: LoadMoreProps | undefined;
}

export function CollectionProjectsTable({
  projects,
  isLoading,
  onSelect,
  openOnly = false,
  emptyMessage = 'No collection project is set up yet',
  emptyHint,
  loadMoreProps,
}: CollectionProjectsTableProps) {
  const columns = useMemo<ColumnDef<QuestionProject, unknown>[]>(
    () => [
      createSerialColumn<QuestionProject>(),
      {
        accessorKey: 'name',
        header: 'Collection Project Name',
        cell: ({ row }) => (
          <WrappingCellText className="font-medium">{row.original.name}</WrappingCellText>
        ),
      },
      {
        accessorKey: 'className',
        header: 'Class',
        cell: ({ row }) => row.original.className ?? '—',
      },
      {
        accessorKey: 'instituteCount',
        header: 'Assigned Institute',
        cell: ({ row }) => (
          <span className="tabular-nums">{formatNumber(row.original.instituteCount)}</span>
        ),
      },
      {
        accessorKey: 'pendingCount',
        header: 'Pending Institute',
        cell: ({ row }) => (
          <span
            className={
              row.original.pendingCount > 0 ? 'tabular-nums text-amber-700' : 'tabular-nums'
            }
          >
            {formatNumber(row.original.pendingCount)}
          </span>
        ),
      },
      {
        accessorKey: 'declareDate',
        header: 'Declare Date',
        cell: ({ row }) => formatDisplayDate(row.original.declareDate),
      },
      {
        accessorKey: 'deadlineDate',
        header: 'Deadline Date',
        cell: ({ row }) => formatDisplayDate(row.original.deadlineDate),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
      },
      createActionsColumn<QuestionProject>((project) => {
        const isClosed = openOnly && !isProjectOpen(project.deadlineDate);
        return (
          <RowActionButton
            label="Select"
            ariaLabel={`Open ${project.name}`}
            onClick={() => onSelect(project.projectId)}
            isDisabled={isClosed}
            disabledHint={isClosed ? getClosedProjectHint(project.deadlineDate) : undefined}
          />
        );
      }),
    ],
    [onSelect, openOnly],
  );

  return (
    <DataTable
      data={projects}
      columns={columns}
      isLoading={isLoading}
      className="lg:flex-1"
      stickyColumnId="name"
      emptyMessage={emptyMessage}
      emptyHint={emptyHint}
      {...loadMoreProps}
    />
  );
}
